/// <amd-module name="Search/Shared/Utils" />

import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import * as SearchSharedContracts from "Search/LegacyWebApi/Contracts/Search.SharedLegacy";

export function deserialize<T extends any>(dataString: string | undefined): T | undefined {
    let object: T = {} as T;

    if (!dataString || dataString === "") {
        return;
    }

    try {
        object = JSON.parse(dataString) as T;
    } catch (error) {
        dataString.split("}")
            .filter(c => !!c)
            .forEach((category: string) => {
                const nameValues = category.split("{");
                if (nameValues.length === 2) {
                    const key: string = nameValues[0];
                    // @ts-ignore TypeScript 4.0 upgrade
                    object[key] = nameValues[1].split("*").filter(s => !!s);
                }
            });
    }

    return object;
}

export function serializeFilters(object: { [id: string]: string[] }): string {
    let filterString = "";
    if (!!object) {
        Object.keys(object)
            .forEach(key => {
                const filterValues = object[key].join("*");
                filterString = `${filterString}${key}{${filterValues}}`;
            });
    }

    return filterString;
}

export function serializeSortOptions(sortOptions: SearchSharedContracts.EntitySortOption[]): string {
    return JSON.stringify(sortOptions);
}

export function areSortOptionsEqual(
    sortOptionsFromUrl: SearchSharedContracts.EntitySortOption[],
    currentSortOptions: SearchSharedContracts.EntitySortOption[]): boolean {
    // If both the sort options are null return true and if any one of them is null return false
    if (!sortOptionsFromUrl && !currentSortOptions) {
        return true;
    }

    if (!sortOptionsFromUrl &&
        currentSortOptions &&
        currentSortOptions.length === 0) {
        return true;
    }

    if (!currentSortOptions
        && sortOptionsFromUrl
        && sortOptionsFromUrl.length === 0) {
        return true;
    }

    if (!sortOptionsFromUrl || !currentSortOptions) {
        return false;
    }

    if (sortOptionsFromUrl.length !== currentSortOptions.length) {
        return false;
    }

    return !sortOptionsFromUrl.some(sortOptionInUrl => {
        return !currentSortOptions.some(currentSortOption => currentSortOption.field.toLowerCase() === sortOptionInUrl.field.toLowerCase() &&
            currentSortOption.sortOrder.toLowerCase() === sortOptionInUrl.sortOrder.toLowerCase());
    });
}

export function areFiltersEqual(left: { [key: string]: string[] }, right: { [key: string]: string[] }): boolean {
    deleteEmptyFilters(left);
    deleteEmptyFilters(right);
    // If both the left and right are null return true and if any one of them is null return false
    if (!left && !right) {
        return true;
    }

    if (!left && right && Object.keys(right).length === 0) {
        return true;
    }

    if (!right && left && Object.keys(left).length === 0) {
        return true;
    }

    if (!left || !right) {
        return false;
    }

    const leftKeys = Object.keys(left),
        rightKeys = Object.keys(right);

    if (leftKeys.length !== rightKeys.length) {
        return false;
    }

    // keys in both left and right arrays objects should be same.
    const areKeysEqual = !leftKeys.some(lk => rightKeys.indexOf(lk) < 0);

    if (!areKeysEqual) {
        return false;
    }

    // There doesn't exist a key in leftKeys for which the corresponding array of values in "right" has different number of items or are un-equal
    const areFiltersEqual = !leftKeys.some(lk => left[lk].length !== right[lk].length || left[lk].some(lkv => right[lk].indexOf(lkv) < 0));

    return areFiltersEqual;
}

function deleteEmptyFilters(filters: { [key: string]: string[] }) {
    for (const key in filters) {
        if (filters[key].length <= 0) {
            delete filters[key];
        }
    }
}

/**
 * Compares two string for the occurrence of the searchText
 * Ranks string with exact match higher, then the one in which searchText substring occurs quite early
 * if there is a tie, ranks the two strings based on lexicographic order.
 * @param first
 * @param second
 * @param searchText
 */
export function compare(first: string, second: string, searchText: string): number {
    first = first.toLowerCase();
    second = second.toLowerCase();
    searchText = searchText.toLowerCase();

    if (first === searchText) {
        return -1;
    }

    if (second === searchText) {
        return 1;
    }

    const substringIndexFirst = first.indexOf(searchText);
    const substringIndexSecond = second.indexOf(searchText);

    if (substringIndexFirst !== substringIndexSecond) {
        return substringIndexFirst - substringIndexSecond;
    } else {
        return localeIgnoreCaseComparer(first, second);
    }
}

export function getWindowTitle(entityLabel: string, searchText?: string): string {
    return searchText
        ? `${searchText} - ${entityLabel}`
        : `${entityLabel}`;
}

/**
 * HTML Encodes the string. Use this method to help prevent cross site scripting attacks
 * by cleaning text which may contain HTML elements before the string is display in a web page.
 * 	
 * 
 * @param str The string to be encoded
 * @return A copy of the current string which has been HTML encoded
 */
export function htmlEncode(str: string): string {

    // we can write a slightly faster (30%) version that uses a regexp to find and replace
    // all occurences of [&<>"] by their corresponding HTML entities but this approach
    // uses the DOM implementation, so presumably it covers all input/edge cases.

    var div = document.createElement("div");
    div.appendChild(document.createTextNode(str));

    // The trick we are using here doesnt encode quotes. So we have to replace them using regexp search
    return div.innerHTML
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

export function stripOffRelevance(
    sortOptions: SearchSharedContracts.EntitySortOption[],
    relevance: string): SearchSharedContracts.EntitySortOption[] {
    return sortOptions.filter(so => so.field !== relevance);
}